import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AssessmentInfo } from "@/types/assessment";

interface RenameAssessmentDialogProps {
  assessment: AssessmentInfo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRename: (assessmentId: string, newTitle: string) => Promise<void>;
}

export function RenameAssessmentDialog({ 
  assessment, 
  open, 
  onOpenChange, 
  onRename 
}: RenameAssessmentDialogProps) {
  const [title, setTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update title when assessment changes
  useEffect(() => {
    if (assessment) {
      setTitle(assessment.title);
    }
  }, [assessment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assessment || !title.trim()) return;

    setIsSubmitting(true);
    try {
      await onRename(assessment.id, title.trim());
      onOpenChange(false);
    } catch (error) {
      console.error("Error renaming assessment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rename assessment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-semibold">
              Assessment title
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Placeholder"
              required
              className="h-11"
            />
          </div>
          <DialogFooter className="mt-6" >
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !title.trim()}>
              {isSubmitting ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
